// The client page — '#client?id=N'. THE STANDING BRIEF.
//
// One payload, one column, one job: answer "where does this client stand, and what is the next
// move" inside three seconds, and route to wherever the answer can be acted on.
//
//   masthead   the client's name is the headline; the RAIL takes the deck slot
//   rail       five scalar fields — ENGAGEMENTS · DAY · BALL · LAST CONTACT · NEW — fixed set,
//              fixed order, invariant geometry, all five client-scoped
//   zone 1     NEXT MOVE     what to do, ranked, with its evidence and its route
//   zone 2     MOMENTUM      how it has been going (suppressed below 5 touches / 8 weeks)
//   zone 3     LATEST        what just happened, NEW marked from the operator's last visit
//   zone 4     ENGAGEMENTS   one card each — the ONLY zone that may state a phase or a gate
//   zone 5     PEOPLE        contacts on the record + the people the evidence names
//   zone 6     WHAT WE KNOW  the standing facts, by conversation
//
// THE SCOPE RULE (v3.1): anything derived from an engagement_id renders inside a per-engagement
// element; anything derived from the client_id may render at page level. There is no third
// category — which is why the rail counts engagements instead of claiming a phase, and why the
// nine-position track lives on a card that names its subject.
//
// THE DETERMINISM MANDATE: every string on this page is a column value, a number counted from
// columns, or a server-composed template. The page ARRANGES. It does not derive, and it never
// calls a model.
//
// The four-tab layout this replaced is gone. The three RECORD surfaces the brief summarises —
// the full timeline, the library, the client record — are sub-views of this same page, reached
// from the zone that summarises them; everything engagement-scoped routes to its own screen.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../components/Toast.js';
import { useEngagement } from '../context/EngagementContext.js';
import NewEngagementModal from '../components/NewEngagementModal.js';
import ClientTimeline from '../components/ClientTimeline.js';
import ClientLibrary from '../components/ClientLibrary.js';
import ClientFactsPanel from '../components/ClientFactsPanel.js';
import ClientContactsRecord from '../components/ClientContactsRecord.js';
import ClientRail from '../components/ClientRail.js';
import ClientNextMove from '../components/ClientNextMove.js';
import ClientMomentum from '../components/ClientMomentum.js';
import ClientLatest from '../components/ClientLatest.js';
import ClientEngagementCards from '../components/ClientEngagementCards.js';
import ClientPeople from '../components/ClientPeople.js';
import { ApiError } from '../api/client.js';
import { briefApi, type BriefPayload, type BriefRoute, type BriefRouteCell } from '../api/brief.js';
import * as crm from '../api/crm.js';
import { useLocale } from '../i18n/index.js';
import './ClientPage.de.js';
import './ClientPage.fr.js';

/** The record surfaces the brief summarises. 'brief' is the page; the rest are one level in. */
type View = 'brief' | 'timeline' | 'library' | 'record';

const VIEW_TITLE: Record<Exclude<View, 'brief'>, string> = {
  timeline: 'The full timeline',
  library: 'The library',
  record: 'The client record',
};

export default function ClientPage({ clientId }: { clientId: number }) {
  const toast = useToast();
  const { t, fmtDate } = useLocale();
  const { setCurrentId, reload: reloadEngagements } = useEngagement();

  const [brief, setBrief] = useState<BriefPayload | null>(null);
  const [view, setView] = useState<View>('brief');
  /** The commitment ref the BALL cell routed to — the timeline lands on it. */
  const [timelineAnchor, setTimelineAnchor] = useState<string | null>(null);
  const [recordOpen, setRecordOpen] = useState(false);
  const [engModalOpen, setEngModalOpen] = useState(false);
  /** Bumped when a reconstruction pass lands facts — the facts zone reloads on it. */
  const [factsRefresh, setFactsRefresh] = useState(0);

  /**
   * The full client detail. The BRIEF is the page; `detail` is only what the record surfaces
   * need (engagement filters, contact CRUD, the record form), so it loads when one opens —
   * the brief stays one call.
   */
  const [detail, setDetail] = useState<crm.ClientDetail | null>(null);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const loadBrief = useCallback(async () => {
    try {
      const payload = await briefApi.get(clientId);
      if (mounted.current) setBrief(payload);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        toast('warn', t('That client does not exist (anymore).'));
        window.location.hash = 'clients';
        return;
      }
      toast('err', (err as Error).message);
    }
  }, [clientId, toast, t]);

  useEffect(() => { void loadBrief(); }, [loadBrief]);

  const loadDetail = useCallback(async () => {
    try {
      const d = await crm.getClient(clientId);
      if (mounted.current) setDetail(d);
    } catch (err) {
      toast('err', (err as Error).message);
    }
  }, [clientId, toast]);

  /** Opening a record surface needs the detail; the brief alone does not. */
  const openView = useCallback((next: Exclude<View, 'brief'>, anchor: string | null = null) => {
    setTimelineAnchor(anchor);
    setView(next);
    if (!detail) void loadDetail();
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [detail, loadDetail]);

  const backToBrief = useCallback(() => {
    setView('brief');
    setTimelineAnchor(null);
    void loadBrief();
  }, [loadBrief]);

  // ── routing ────────────────────────────────────────────────────────────────────
  // Every route on this page passes through here. A field's or a rule's route is a CONSTANT
  // property of the field or the rule (only anchors carry refs), so the mapping is a lookup
  // and never a decision made from a value.

  const liveEngagements = (brief?.engagements ?? []).filter((e) => e.archived_at === null);

  const openScreen = useCallback((screen: string, engagementId?: number | null, anchor?: string | null) => {
    // Engagement-scoped screens operate on the CURRENT engagement — set it before the hash
    // changes, or the target page opens on whatever was selected last.
    const target = engagementId ?? (liveEngagements.length === 1 ? liveEngagements[0].id : null);
    if (target) setCurrentId(target);
    window.location.hash = anchor ? `${screen}${anchor}` : screen;
  }, [liveEngagements, setCurrentId]);

  const onRoute = useCallback((route: BriefRoute) => {
    if (!route) return;
    if (route.kind === 'in-page') {
      const node = document.querySelector(route.anchor);
      if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (route.kind === 'related') {
      const related = route.related as { screen?: string; engagement_id?: number };
      if (related?.screen) openScreen(related.screen, related.engagement_id ?? null);
      else toast('warn', t('This task carries no route — open it on the engagement it belongs to.'));
      return;
    }
    // 'screen'
    if (route.screen === 'timeline') { openView('timeline', route.anchor ?? null); return; }
    if (route.screen === 'engagements') { setEngModalOpen(true); return; }
    openScreen(route.screen, route.engagement_id ?? null, route.anchor ?? null);
  }, [openScreen, openView, toast, t]);

  const openEngagement = useCallback((id: number) => {
    setCurrentId(id);
    window.location.hash = 'lifecycle';
  }, [setCurrentId]);

  const onRouteCell = useCallback((engagementId: number, cell: BriefRouteCell) => {
    setCurrentId(engagementId);
    window.location.hash = cell.screen;
  }, [setCurrentId]);

  // ── loading ────────────────────────────────────────────────────────────────────

  if (!brief) {
    return (
      <div className="page client-page">
        <a className="client-back" href="#clients">{t('← Clients')}</a>
        <div className="flex justify-center p-24"><div className="spinner spinner-lg" /></div>
      </div>
    );
  }

  const client = brief.client;
  const multi = liveEngagements.length > 1;
  const sponsor = brief.people.contacts.find((c) => c.is_sponsor) ?? null;
  const firstContactField = brief.readout.find((f) => f.key === 'day');
  const firstContact = firstContactField && firstContactField.detail.startsWith('first contact')
    ? firstContactField.detail.replace('first contact ', '')
    : null;
  const idBits = [client.sector, [client.city, client.country].filter(Boolean).join(', ')].filter(Boolean);

  // ── a record surface ───────────────────────────────────────────────────────────

  if (view !== 'brief') {
    return (
      <div className="page client-page">
        <a className="client-back" href="#clients">{t('← Clients')}</a>
        <div className="client-subview-head">
          <h2>{t(VIEW_TITLE[view])}</h2>
          <span className="zone-count voice-figure">{client.name}</span>
          <span className="ml-auto" />
          <button type="button" className="btn btn-sm" onClick={backToBrief}>{t('← Back to the brief')}</button>
        </div>
        {!detail ? (
          <div className="flex justify-center p-24"><div className="spinner spinner-lg" /></div>
        ) : view === 'timeline' ? (
          <ClientTimeline
            key={detail.id}
            detail={detail}
            anchorRef={timelineAnchor}
            onFactsChanged={() => setFactsRefresh((n) => n + 1)}
            onCommitmentChanged={() => void loadBrief()}
          />
        ) : view === 'library' ? (
          <ClientLibrary detail={detail} />
        ) : (
          <ClientContactsRecord
            detail={detail}
            onChanged={() => { void loadDetail(); void loadBrief(); }}
            onDeleted={() => { window.location.hash = 'clients'; }}
          />
        )}
      </div>
    );
  }

  // ── the brief ──────────────────────────────────────────────────────────────────

  return (
    <div className="page client-page">
      <a className="client-back" href="#clients">{t('← Clients')}</a>

      {/* ── The masthead. The sub-line carries the client's own identity: the rail's first
             cell already states the engagements, and a fact appears exactly once. ── */}
      <header className="mast">
        <div className="mast-top">
          <div className="mast-main">
            <h1 className="client-mast-h1">
              {client.name}
              <i className={`client-statedot${client.status === 'active' ? '' : ' client-statedot--idle'}`} aria-hidden="true" />
              <span className="client-statelabel">{t(client.status)}</span>
            </h1>
            <div className="mast-sub">
              {client.short_name && <span className="src-ref">{client.short_name}</span>}
              {idBits.length > 0 && <span>{idBits.join(' · ')}</span>}
              {!client.short_name && idBits.length === 0 && (
                <span className="n-none">{t('nothing on the client record yet')}</span>
              )}
            </div>
          </div>
          <div className="mast-verbs">
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setRecordOpen((v) => !v)}>
              {t('Record')} {recordOpen ? '▾' : '▸'}
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setEngModalOpen(true)}>
              {t('New engagement')}
            </button>
          </div>
        </div>
      </header>

      <ClientRail readout={brief.readout} onRoute={onRoute} />

      <div>
        {recordOpen && (
          <div className="sheet client-record">
            <dl className="client-recdl">
              <dt>{t('Legal name')}</dt><dd>{client.legal_name ?? <span className="n-none">—</span>}</dd>
              <dt>{t('Short name')}</dt><dd>{client.short_name ? <span className="src-ref">{client.short_name}</span> : <span className="n-none">—</span>}</dd>
              <dt>{t('Sector')}</dt><dd>{client.sector ?? <span className="n-none">—</span>}</dd>
              <dt>{t('Address')}</dt>
              <dd>
                {client.address_line1 || client.city ? (
                  <>
                    {client.address_line1}{client.address_line2 ? `, ${client.address_line2}` : ''}<br />
                    <span className="voice-figure">{client.postcode ?? '—'}</span> {client.city ?? '—'}, {client.country ?? '—'}
                  </>
                ) : <span className="n-none">—</span>}
              </dd>
              <dt>{t('First contact')}</dt><dd className="voice-figure">{firstContact ?? <span className="n-none">{t('no dated evidence')}</span>}</dd>
              <dt>{t('On the record since')}</dt><dd className="voice-figure">{fmtDate(client.created_at)}</dd>
              <dt>{t('Sponsor')}</dt><dd>{sponsor ? sponsor.name : <span className="n-none">{t('nobody named')}</span>}</dd>
            </dl>
            <div style={{ marginTop: 'var(--s12)' }}>
              <button type="button" className="btn btn-sm" onClick={() => openView('record')}>{t('Open the record →')}</button>
            </div>
          </div>
        )}
      </div>

      <ClientNextMove queue={brief.queue} multi={multi} onRoute={onRoute} />

      <ClientMomentum momentum={brief.momentum} firstContactLabel={firstContact} />

      <ClientLatest
        latest={brief.latest}
        latestSince={brief.latest_since}
        firstVisitWindow={(brief.readout.find((f) => f.key === 'new')?.detail ?? '').startsWith('new in the last')}
        onOpenTimeline={() => openView('timeline')}
        onOpenLibrary={() => openView('library')}
      />

      <ClientEngagementCards
        engagements={brief.engagements}
        onNewEngagement={() => setEngModalOpen(true)}
        onOpen={openEngagement}
        onRouteCell={onRouteCell}
      />

      <ClientPeople
        clientId={clientId}
        contacts={brief.people.contacts}
        ghosts={brief.people.ghosts}
        onChanged={() => { void loadBrief(); if (detail) void loadDetail(); }}
        onOpenRecord={() => openView('record')}
      />

      <ClientFactsPanel clientId={clientId} refreshToken={factsRefresh} />

      <NewEngagementModal
        open={engModalOpen}
        onClose={() => setEngModalOpen(false)}
        clientId={clientId}
        onCreated={(created) => {
          void loadBrief();
          void reloadEngagements();
          setCurrentId(created.id);
          toast('ok', t('{code} created — open it from the switcher or the Lifecycle page', { code: created.code }));
        }}
      />
    </div>
  );
}
